import { apiClient, baseUrl } from './api.js';
import { unwrap } from './api-error.js';
import type { ApiPaths } from '@reos/api-client';

/**
 * Fakturujici subjekt (krok FAK1): typovany klient nad `/v1/finance/billing/*`.
 * Odpovedi jsou `zod.any()` (viz packages/finance/src/public/plugin.ts), DTO
 * typy jsou proto psane rucne podle `plugin.ts` (supplierToDto/brandToDto/
 * overviewToDto); request body chodi z generovaneho OpenAPI kontraktu.
 */

export type BillerMode = 'office' | 'broker';
export type SupplierStatus = 'draft' | 'provisioning' | 'active' | 'error';
export type LogoChoice = 'undecided' | 'logo' | 'none';
export type BillingStatusView = 'unconfigured' | 'notStarted' | 'preparing' | 'active' | 'failed';
export type ReadinessStepKey = 'company' | 'bank' | 'brand' | 'firstInvoice';

export interface SupplierDto {
  readonly id: string;
  readonly brokerUserId: string | null;
  readonly companyName: string;
  readonly ico: string;
  readonly dic: string | null;
  readonly vatPayer: boolean;
  readonly street: string | null;
  readonly city: string | null;
  readonly zip: string | null;
  readonly email: string | null;
  readonly bankAccountIban: string | null;
  /**
   * Krok FIX6 bod 2: tentyz ucet v narodnim tvaru („115-2595610287/0100").
   * Pocita `@reos/finance` z IBANu; `null` u neceskeho IBANu nebo bez uctu.
   */
  readonly bankAccountLocal: string | null | undefined;
  readonly bankAccountVerified: boolean;
  /** Krok FAK4: je ucet i v MyUctu? Bez toho by faktura nesla jiny ucet. */
  readonly bankSynced: boolean;
  readonly bankSyncError: string | null;
  readonly status: SupplierStatus;
  readonly lastError: string | null;
}

export interface BrandDto {
  readonly accentColor: string | null;
  readonly logoChoice: LogoChoice;
  readonly colorsConfirmed: boolean;
  readonly tagline: string | null;
  readonly footerText: string | null;
  readonly businessName: string | null;
  readonly inheritOfficeBrand: boolean;
  readonly applied: boolean;
  readonly applyError: string | null;
}

/** Krok FAK4: logo, ktere pujde na doklad (bajty se tahaji zvlast z `.../logo`). */
export interface SupplierLogoDto {
  readonly contentType: string;
  readonly filename: string;
  readonly byteSize: number;
  readonly width: number | null;
  readonly height: number | null;
  readonly source: 'upload' | 'office';
}

export type SyncKind = 'brand' | 'bank';

/**
 * Krok FAK4: tri stavy odeslani do MyUcta. `queued` je ten, ktery do FAK4
 * chybel - "ulozeno, ale nikam neodeslano" se tvarilo jako uspech.
 */
export type SyncDelivery =
  | { readonly state: 'applied' }
  | { readonly state: 'failed'; readonly error: string }
  | { readonly state: 'queued'; readonly reason: 'not_connected' | 'retry' };

export interface SyncJobDto {
  readonly kind: SyncKind;
  readonly status: 'pending' | 'running' | 'done' | 'error';
  readonly attempts: number;
  readonly lastError: string | null;
}

export interface ReadinessStepDto {
  readonly key: ReadinessStepKey;
  readonly done: boolean;
  readonly weight: number;
  readonly blocking: boolean;
}

export interface ReadinessDto {
  readonly percent: number;
  readonly steps: readonly ReadinessStepDto[];
  readonly complete: boolean;
  readonly canInvoice: boolean;
  readonly open: ReadinessStepKey | null;
}

export interface BillingOverviewDto {
  readonly billerMode: BillerMode;
  readonly configured: boolean;
  readonly status: BillingStatusView;
  readonly supplier: SupplierDto | null;
  readonly brand: BrandDto | null;
  readonly readiness: ReadinessDto;
  /** SPAYD text vzorove platby na ucet dodavatele - `null`, dokud ucet neni. */
  readonly bankQrText: string | null;
  readonly logo: SupplierLogoDto | null;
  /** Id dodavatele kancelare, od ktereho se brand dedi; `null` = vlastni brand. */
  readonly brandInheritedFrom: string | null;
  readonly officeLogoAvailable: boolean;
  readonly syncJobs: readonly SyncJobDto[];
}

export interface AresCompanyDto {
  readonly ico: string;
  readonly companyName: string;
  readonly street: string;
  readonly city: string;
  readonly zip: string;
  /** FIX6 nalez 9a - `null` = ARES pro tohle ICO DIC nema (typicky neplatce DPH). */
  readonly dic: string | null;
}

export type CreateSupplierBody =
  ApiPaths['/v1/finance/billing/supplier']['post']['requestBody']['content']['application/json'];
export type UpdateBrandBody =
  ApiPaths['/v1/finance/billing/supplier/{id}/brand']['put']['requestBody']['content']['application/json'];

/** `brokerUserId` - krok FAK5 bod 3: admin smi precist dodavatele KONKRETNIHO maklere ("druhy dodavatel"). */
export async function getBillingOverview(brokerUserId?: string): Promise<BillingOverviewDto> {
  return (await unwrap(
    apiClient.GET('/v1/finance/billing/overview', { params: { query: brokerUserId !== undefined ? { brokerUserId } : {} } }),
  )) as BillingOverviewDto;
}

/** Krok FAK5 bod 3 - vsichni dodavatele tenanta (kancelar + kazdy makler OSVC); jen pro admin obrazovku "Druhy dodavatel". */
export async function listSuppliers(): Promise<SupplierDto[]> {
  return (await unwrap(apiClient.GET('/v1/finance/billing/suppliers', {}))) as SupplierDto[];
}

export async function updateBillerMode(billerMode: BillerMode): Promise<{ billerMode: BillerMode }> {
  return (await unwrap(apiClient.PUT('/v1/finance/billing/settings', { body: { billerMode } }))) as { billerMode: BillerMode };
}

/** `null` = ARES autoritativne rekl "takove ICO neexistuje". */
export async function lookupAresCompany(ico: string): Promise<AresCompanyDto | null> {
  const { data, error, response } = await apiClient.GET('/v1/finance/billing/ares/{ico}', { params: { path: { ico } } });
  if (response.status === 404) return null;
  if (!response.ok) return unwrap(Promise.resolve({ data, error, response })) as never;
  return data as AresCompanyDto;
}

export async function createOrRetrySupplier(body: CreateSupplierBody): Promise<SupplierDto> {
  return (await unwrap(apiClient.POST('/v1/finance/billing/supplier', { body }))) as SupplierDto;
}

export async function setSupplierBankAccount(
  supplierId: string,
  account: string,
): Promise<{ supplier: SupplierDto; iban: string; local: string | null; qrText: string | null; delivery: SyncDelivery }> {
  return (await unwrap(
    apiClient.PUT('/v1/finance/billing/supplier/{id}/bank', { params: { path: { id: supplierId } }, body: { account } }),
  )) as { supplier: SupplierDto; iban: string; local: string | null; qrText: string | null; delivery: SyncDelivery };
}

export async function updateSupplierBrand(
  supplierId: string,
  body: UpdateBrandBody,
): Promise<{ brand: BrandDto; delivery: SyncDelivery; logo: SupplierLogoDto | null }> {
  return (await unwrap(
    apiClient.PUT('/v1/finance/billing/supplier/{id}/brand', { params: { path: { id: supplierId } }, body }),
  )) as { brand: BrandDto; delivery: SyncDelivery; logo: SupplierLogoDto | null };
}

// ──────────────────────────────────────────── krok FAK4: logo a nahled dokladu

export interface UploadLogoResultDto {
  readonly logo: SupplierLogoDto;
  readonly delivery: SyncDelivery | null;
}

export async function uploadSupplierLogo(supplierId: string, contentBase64: string, filename: string | null): Promise<UploadLogoResultDto> {
  return (await unwrap(
    apiClient.POST('/v1/finance/billing/supplier/{id}/logo', {
      params: { path: { id: supplierId } },
      body: { contentBase64, filename },
    }),
  )) as UploadLogoResultDto;
}

export async function useOfficeLogo(supplierId: string): Promise<UploadLogoResultDto> {
  return (await unwrap(
    apiClient.POST('/v1/finance/billing/supplier/{id}/logo/from-office', { params: { path: { id: supplierId } } }),
  )) as UploadLogoResultDto;
}

export async function removeSupplierLogo(supplierId: string): Promise<{ delivery: SyncDelivery | null }> {
  return (await unwrap(
    apiClient.DELETE('/v1/finance/billing/supplier/{id}/logo', { params: { path: { id: supplierId } } }),
  )) as { delivery: SyncDelivery | null };
}

export async function retrySupplierSync(supplierId: string, kind: SyncKind): Promise<{ delivery: SyncDelivery }> {
  return (await unwrap(
    apiClient.POST('/v1/finance/billing/supplier/{id}/sync/retry', { params: { path: { id: supplierId } }, body: { kind } }),
  )) as { delivery: SyncDelivery };
}

/**
 * Logo a nahled dokladu jsou BINARKY - `apiClient` (openapi-fetch) ceka JSON,
 * takze se tahaji `fetch`em, stejny vzor jako `downloadInvoicePdf`
 * v `invoices-api.ts`. `credentials: 'include'` je povinne: session je cookie.
 */
export async function fetchSupplierLogo(supplierId: string): Promise<Blob | null> {
  const res = await fetch(`${baseUrl}/v1/finance/billing/supplier/${supplierId}/logo`, { credentials: 'include' });
  if (res.status === 404) return null;
  if (!res.ok) throw new Error(`HTTP ${res.status}`);
  return res.blob();
}

export async function fetchSupplierPreviewPdf(supplierId: string): Promise<Blob> {
  // POST: nahled zaklada draft v uctarne, viz plugin.ts.
  const res = await fetch(`${baseUrl}/v1/finance/billing/supplier/${supplierId}/preview.pdf`, {
    method: 'POST',
    credentials: 'include',
  });
  if (!res.ok) throw new Error(`HTTP ${res.status}`);
  return res.blob();
}
